import React, { useEffect, useMemo, useState } from 'react';
import {
  deleteCompanyWorkflow,
  fetchCompanyWorkflows,
  upsertCompanyWorkflow,
} from '../../services/companyCustomizationApi';
import type { CompanyWorkflow } from '../../services/companyCustomizationApi';
import { safeJsonParse, toPrettyJson } from '../../utils/json';
import { useI18n } from '../../contexts/I18nContext';

interface CompanyWorkflowsManagerProps {
  companyId: string;
}

const CompanyWorkflowsManager: React.FC<CompanyWorkflowsManagerProps> = ({ companyId }) => {
  const { t } = useI18n();
  const [workflows, setWorkflows] = useState<CompanyWorkflow[]>([]);
  const [selectedModule, setSelectedModule] = useState('');
  const [definitionText, setDefinitionText] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const sortedModules = useMemo(() => workflows.map((workflow) => workflow.module).sort(), [workflows]);

  const loadWorkflows = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchCompanyWorkflows(companyId);
      setWorkflows(data);
      if (data.length > 0 && !selectedModule) {
        const first = data[0];
        setSelectedModule(first.module);
        setDefinitionText(toPrettyJson(first.definition));
        setIsActive(first.isActive);
      }
    } catch (err) {
      console.error('[CompanyWorkflowsManager] Failed to load workflows', err);
      setError('Não foi possível carregar workflows configurados.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setSelectedModule('');
    setDefinitionText('');
    setIsActive(true);
    loadWorkflows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const handleSelectModule = (module: string) => {
    setMessage(null);
    setError(null);
    setSelectedModule(module);
    const workflow = workflows.find((item) => item.module === module);
    if (workflow) {
      setDefinitionText(toPrettyJson(workflow.definition));
      setIsActive(workflow.isActive);
    } else {
      setDefinitionText('');
      setIsActive(true);
    }
  };

  const handleSave = async (event: React.FormEvent) => {
    event.preventDefault();
    setMessage(null);
    setError(null);

    if (!selectedModule.trim()) {
      setError(t('customization.workflows.moduleRequired'));
      return;
    }

    let parsedDefinition: Record<string, unknown> = {};
    try {
      parsedDefinition =
        definitionText.trim() === ''
          ? {}
          : safeJsonParse<Record<string, unknown>>(definitionText, parsedDefinition);
    } catch {
      setError(t('customization.workflows.invalidJson'));
      return;
    }

    setSaving(true);
    try {
      await upsertCompanyWorkflow(companyId, selectedModule.trim(), {
        definition: parsedDefinition,
        isActive,
      });
      await loadWorkflows();
      setMessage('Workflow salvo com sucesso.');
    } catch (err) {
      console.error('[CompanyWorkflowsManager] Failed to save workflow', err);
      setError(t('customization.workflows.errorSave'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (module: string) => {
    if (!window.confirm(`Remover workflow do módulo "${module}"?`)) {
      return;
    }
    setMessage(null);
    setError(null);
    setSaving(true);
    try {
      await deleteCompanyWorkflow(companyId, module);
      await loadWorkflows();
      if (selectedModule === module) {
        setSelectedModule('');
        setDefinitionText('');
        setIsActive(true);
      }
      setMessage('Workflow removido.');
    } catch (err) {
      console.error('[CompanyWorkflowsManager] Failed to delete workflow', err);
      setError(t('customization.workflows.errorRemove'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(220px, 280px) 1fr',
          gap: '20px',
        }}
      >
        <div
          style={{
            border: '1px solid #e5e7eb',
            borderRadius: '10px',
            backgroundColor: '#fff',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              padding: '16px',
              borderBottom: '1px solid #f3f4f6',
              fontWeight: 600,
              fontSize: '15px',
            }}
          >
            Módulos configurados
          </div>
          <div style={{ maxHeight: '320px', overflowY: 'auto' }}>
            {loading ? (
              <div style={{ padding: '16px', color: '#6b7280' }}>{t('customization.workflows.loading')}</div>
            ) : sortedModules.length === 0 ? (
              <div style={{ padding: '16px', color: '#6b7280', fontSize: '14px' }}>
                Nenhum workflow configurado. Adicione um módulo abaixo.
              </div>
            ) : (
              sortedModules.map((module) => {
                const workflow = workflows.find((item) => item.module === module);
                return (
                  <div
                    key={module}
                    style={{
                      borderBottom: '1px solid #f3f4f6',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '12px 16px',
                      backgroundColor: module === selectedModule ? '#eef2ff' : '#fff',
                      cursor: 'pointer',
                    }}
                    onClick={() => handleSelectModule(module)}
                  >
                    <div>
                      <div style={{ fontWeight: 600, fontSize: '14px' }}>{module}</div>
                      <div style={{ fontSize: '12px', color: '#6b7280' }}>
                        {workflow?.updatedAt
                          ? `Atualizado em ${new Date(workflow.updatedAt).toLocaleDateString()}`
                          : 'Nunca atualizado'}
                      </div>
                    </div>
                    {workflow && (
                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          handleDelete(module);
                        }}
                        style={{
                          border: 'none',
                          background: 'transparent',
                          color: '#ef4444',
                          cursor: 'pointer',
                          fontSize: '12px',
                        }}
                      >{t('actions.remove')}</button>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>

        <form
          onSubmit={handleSave}
          style={{
            border: '1px solid #e5e7eb',
            borderRadius: '12px',
            padding: '20px',
            backgroundColor: '#fff',
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
          }}
        >
          <div>
            <h3 style={{ margin: '0 0 6px 0' }}>{t('customization.workflows.editor')}</h3>
            <p style={{ margin: 0, color: '#6b7280', fontSize: '13px' }}>
              Defina os estados do módulo (ex: agendamentos, contratos) em formato JSON.
            </p>
          </div>

          {(message || error) && (
            <div
              style={{
                padding: '10px 12px',
                borderRadius: '6px',
                border: message ? '1px solid #86efac' : '1px solid #fecaca',
                backgroundColor: message ? '#dcfce7' : '#fee2e2',
                color: message ? '#166534' : '#b91c1c',
              }}
            >
              {message || error}
            </div>
          )}

          <label style={{ display: 'flex', flexDirection: 'column', gap: '6px', fontSize: '14px' }}>
            Módulo *
            <input
              type="text"
              value={selectedModule}
              onChange={(event) => handleSelectModule(event.target.value)}
              placeholder="ex: appointments"
              required
              style={{
                padding: '10px 12px',
                borderRadius: '6px',
                border: '1px solid #d1d5db',
              }}
            />
          </label>

          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '14px',
            }}
          >
            <input
              type="checkbox"
              checked={isActive}
              onChange={(event) => setIsActive(event.target.checked)}
            />
            Workflow ativo para os usuários da empresa
          </label>

          <label style={{ display: 'flex', flexDirection: 'column', gap: '6px', fontSize: '14px' }}>
            Definição do workflow (JSON)
            <textarea
              value={definitionText}
              onChange={(event) => setDefinitionText(event.target.value)}
              rows={12}
              placeholder={`{
  "steps": ["scheduled", "confirmed", "completed"],
  "transitions": {
    "scheduled": ["confirmed", "cancelled"]
  }
}`}
              style={{
                width: '100%',
                padding: '10px 12px',
                borderRadius: '6px',
                border: '1px solid #d1d5db',
                fontFamily: 'monospace',
                fontSize: '13px',
              }}
            />
          </label>

          <button
            type="submit"
            disabled={saving}
            style={{
              padding: '12px 18px',
              borderRadius: '8px',
              border: 'none',
              backgroundColor: '#16a34a',
              color: '#fff',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            {saving ? 'Salvando...' : 'Salvar workflow'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default CompanyWorkflowsManager;

